import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { defaultFeeScheduleFilters } from '../components/fee-schedules/fee-schedule-filter-bar';
import {
  EntityOption,
  FeeScheduleForm,
} from '../components/fee-schedules/fee-schedule-form/form';
import { FeeScheduleFormValues } from '../components/fee-schedules/fee-schedule-form/validation-schema';
import {
  FeeScheduleGrid,
  type FeeScheduleGridHandle,
} from '../components/fee-schedules/fee-schedule-grid';
import {
  FeeScheduleView,
  FeeScheduleViewToggle,
} from '../components/fee-schedules/fee-schedule-view-toggle';
import { routes } from '../constants/strings/routes';
import {
  DynamicDrawerProvider,
  useDynamicDrawerContext,
} from '../context/dynamic-drawer.context';
import { useSnackbarContext } from '../context/snackbar.context';
import { useBillingWriteAccess } from '../hooks/use-billing-write-access';
import { useFilterUrlSync } from '../hooks/use-filter-url-sync';
import {
  FeeScheduleEntityType,
  PaymentSystemType,
  PaymentType,
  useCreateFeeScheduleMutation,
  useGetAllClientsQuery,
  useGetPartnershipsQuery,
  useGetPaymentTypesQuery,
} from '../types';

const ENTITY_TYPE_TO_VIEW: Record<FeeScheduleEntityType, FeeScheduleView> = {
  [FeeScheduleEntityType.Client]: 'client',
  [FeeScheduleEntityType.Partnership]: 'partnership',
};

const EMPTY_PAYMENT_TYPES: PaymentType[] = [];

export const FEE_SCHEDULES_TEST_ID = 'fee-schedules-container';

const FeeSchedulesContent = ({
  entityType,
}: {
  entityType: FeeScheduleEntityType;
}): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const [filterParams, setFilterParams] = useFilterUrlSync(
    defaultFeeScheduleFilters,
  );

  const { data: clientsData, loading: clientsLoading } = useGetAllClientsQuery({
    skip: entityType !== FeeScheduleEntityType.Client,
  });

  const { data: partnershipsData, loading: partnershipsLoading } =
    useGetPartnershipsQuery({
      variables: { type: 'billing' },
      skip: entityType !== FeeScheduleEntityType.Partnership,
    });

  const clients = useMemo(
    () => clientsData?.getAllClients ?? [],
    [clientsData],
  );
  const partnerships = useMemo(
    () => partnershipsData?.getPartnerships ?? [],
    [partnershipsData],
  );

  const entities: EntityOption[] = useMemo(() => {
    if (entityType === FeeScheduleEntityType.Client) {
      return clients.map(c => ({ id: c.id, name: c.name }));
    }
    return partnerships.map(p => ({ id: p.id, name: p.name }));
  }, [entityType, clients, partnerships]);

  const entitiesLoading =
    entityType === FeeScheduleEntityType.Client
      ? clientsLoading
      : partnershipsLoading;

  const { data: paymentTypesData, loading: paymentTypesLoading } =
    useGetPaymentTypesQuery();
  const paymentTypes = paymentTypesData?.getPaymentTypes ?? EMPTY_PAYMENT_TYPES;

  const refetchGridRef = useRef<FeeScheduleGridHandle>(null);

  const [createFeeSchedule] = useCreateFeeScheduleMutation();

  const handleCreate = useCallback(
    async (values: FeeScheduleFormValues): Promise<void> => {
      try {
        await createFeeSchedule({
          variables: {
            feeScheduleInput: {
              entityType,
              entityId: values.entityId as number,
              paymentType: values.paymentType,
              paymentSystem: values.paymentSystem as PaymentSystemType,
              price: values.price as number,
              enabled: values.enabled,
              notes: values.notes || null,
            },
          },
        });
        setSnackbarState('Fee schedule created successfully', 'success', 3000);
        onClose();
        refetchGridRef.current?.reload();
      } catch {
        setSnackbarState('Error creating fee schedule', 'error', 3000);
      }
    },
    [createFeeSchedule, entityType, setSnackbarState, onClose],
  );

  const drawerTitle =
    entityType === FeeScheduleEntityType.Client
      ? 'Add Client Fee'
      : 'Add Partnership Fee';

  const handleAddRecord = useCallback((): void => {
    setDrawerContent(
      <HHStack padding={4}>
        <FeeScheduleForm
          onSubmit={handleCreate}
          entityType={entityType}
          entities={entities}
          entitiesLoading={entitiesLoading}
          paymentTypes={paymentTypes}
          paymentTypesLoading={paymentTypesLoading}
        />
      </HHStack>,
      <HHTypography hhVariant="h6">{drawerTitle}</HHTypography>,
      undefined,
      'temporary',
    );
  }, [
    setDrawerContent,
    handleCreate,
    entityType,
    entities,
    entitiesLoading,
    paymentTypes,
    paymentTypesLoading,
    drawerTitle,
  ]);

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={4}
      minHeight="0"
      data-testid={FEE_SCHEDULES_TEST_ID}
    >
      <HHStack
        sx={{ paddingLeft: 6, paddingRight: 6, paddingTop: 6 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHStack direction="row" alignItems="center" spacing={5}>
          <HHStack sx={{ minWidth: 320 }}>
            <HHTypography hhVariant="section-title">
              {entityType === FeeScheduleEntityType.Client
                ? 'Client Fee Schedules'
                : 'Partnership Fee Schedules'}
            </HHTypography>
          </HHStack>
          <FeeScheduleViewToggle
            currentView={ENTITY_TYPE_TO_VIEW[entityType]}
          />
        </HHStack>
        {canWrite && (
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={handleAddRecord}
            data-testid="add-fee-schedule-button"
            sx={{ minWidth: 120 }}
          >
            Add Fee
          </HHButton>
        )}
      </HHStack>
      <FeeScheduleGrid
        entityType={entityType}
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        clients={clients}
        clientsLoading={clientsLoading}
        partnerships={partnerships}
        partnershipsLoading={partnershipsLoading}
        paymentTypes={paymentTypes}
        paymentTypesLoading={paymentTypesLoading}
        ref={refetchGridRef}
      />
    </HHStack>
  );
};

const FeeSchedules = (): JSX.Element => {
  const location = useLocation();

  const isPartnership = location.pathname.includes(
    routes.billing.partnershipFeeSchedules,
  );
  const entityType: FeeScheduleEntityType = isPartnership
    ? FeeScheduleEntityType.Partnership
    : FeeScheduleEntityType.Client;

  return (
    <DynamicDrawerProvider>
      <FeeSchedulesContent entityType={entityType} />
    </DynamicDrawerProvider>
  );
};

export default FeeSchedules;
